import React, { useCallback, useEffect, useRef, useState } from 'react';
import Checkbox from '../../design-system/primitives/Checkbox';
import { Button } from '../ui/button';
import Divider from '../../design-system/primitives/Divider';
import Eyebrow from '../../design-system/primitives/Eyebrow';
import Icon from '../../design-system/primitives/Icon';
import Segmented from '../../design-system/primitives/Segmented';
import Toggle from '../../design-system/primitives/Toggle';
import { apiService } from '../../services/api';
import { useToast } from '../../context/ToastContext';

const CADENCE_OPTIONS = [
  { value: 'off', label: 'Off' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'custom', label: 'Custom' },
];

const STAGE_STEPS = [
  { key: 'pulling', label: 'Pulling data' },
  { key: 'preprocessing', label: 'Preprocessing' },
  { key: 'predicting', label: 'Predicting' },
  { key: 'writing', label: 'Writing back' },
];

const TS_STAGE_STEPS = [
  { key: 'pulling', label: 'Pulling data' },
  { key: 'preprocessing', label: 'Preprocessing' },
  { key: 'forecasting', label: 'Forecasting' },
  { key: 'writing', label: 'Writing back' },
];

// Chronos-2's native quantile grid. The backend's GET payload
// (meta.quantile_grid) is authoritative; this is only the fallback.
const QUANTILE_GRID = [
  0.01, 0.05, 0.1, 0.15, 0.2, 0.25, 0.3, 0.35, 0.4, 0.45, 0.5,
  0.55, 0.6, 0.65, 0.7, 0.75, 0.8, 0.85, 0.9, 0.95, 0.99,
];
const DEFAULT_QUANTILES = [0.05, 0.5, 0.95];

const STATUS_COLORS = { success: 'var(--green)', warn: '#d97706', error: 'var(--danger)', running: 'var(--accent)' };

// Every table Qombra creates for writeback is namespaced under this prefix
// (enforced server-side too) so customers can tell what Qombra owns.
const TABLE_PREFIX = 'qombra_';

const inputStyle = {
  padding: '8px 10px', borderRadius: 8, background: 'var(--bg-elev-2)',
  border: '1px solid var(--line-soft)', color: 'var(--fg-strong)', fontSize: 13.5,
  fontFamily: 'var(--font-sans)', outline: 'none', width: '100%', boxSizing: 'border-box',
};

const captionStyle = { fontSize: 12.5, color: 'var(--fg-muted)', lineHeight: 1.45 };

const WEEKDAYS = [
  { value: 1, label: 'Monday' }, { value: 2, label: 'Tuesday' }, { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' }, { value: 5, label: 'Friday' }, { value: 6, label: 'Saturday' },
  { value: 0, label: 'Sunday' },
];

const pad2 = (n) => String(n).padStart(2, '0');
const daysInMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();

// Picker defaults: the existing next run if there is one, otherwise "now + 3
// minutes, today" — so a freshly enabled schedule fires almost immediately.
const anchorFrom = (iso) => {
  const d = iso ? new Date(iso) : new Date(Date.now() + 3 * 60000);
  // monthday caps at 28 so the monthly picker's choice exists in every month.
  return { time: `${pad2(d.getHours())}:${pad2(d.getMinutes())}`, weekday: d.getDay(), monthday: Math.min(d.getDate(), 28) };
};

// Resolve cadence + pickers to the concrete first-run datetime (local → ISO).
// If the chosen moment is already past, roll forward one cadence unit.
const firstRunISO = (cadence, anchor, customDays) => {
  if (!cadence || cadence === 'off') return null;
  const [h, m] = String(anchor.time || '00:00').split(':').map(Number);
  const d = new Date();
  d.setHours(h || 0, m || 0, 0, 0);
  if (cadence === 'weekly') {
    d.setDate(d.getDate() + ((Number(anchor.weekday) - d.getDay() + 7) % 7));
  } else if (cadence === 'monthly') {
    d.setDate(Math.min(Number(anchor.monthday) || 1, daysInMonth(d)));
  }
  if (d.getTime() <= Date.now()) {
    if (cadence === 'weekly') d.setDate(d.getDate() + 7);
    else if (cadence === 'monthly') {
      const day = Number(anchor.monthday) || 1;
      d.setDate(1);
      d.setMonth(d.getMonth() + 1);
      d.setDate(Math.min(day, daysInMonth(d)));
    } else if (cadence === 'custom') d.setDate(d.getDate() + Math.max(1, parseInt(customDays, 10) || 1));
    else d.setDate(d.getDate() + 1); // daily
  }
  return d.toISOString();
};

// Mirrors the backend guardrail (PipelineSchedule.retrain_too_close_to_inference):
// only the retrain-before-inference direction within MIN_RETRAIN_GAP_MINUTES is
// flagged, compared by local time-of-day (cyclically) — both cadence pickers
// enter a local clock time, so comparing that directly avoids a UTC round-trip.
// "Same schedule" mode always fixes a 30-minute lead server-side, so it never
// needs this check.
const MIN_RETRAIN_GAP_MINUTES = 20;
const timeToMinutes = (hhmm) => {
  const [h, m] = String(hhmm || '00:00').split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
};
const retrainTooCloseToInference = (form) => {
  if (form.retrain_same_as_inference) return false;
  if (form.inference_cadence === 'off' || form.retrain_cadence === 'off') return false;
  const gap = (timeToMinutes(form.inference_anchor.time) - timeToMinutes(form.retrain_anchor.time) + 1440) % 1440;
  return gap > 0 && gap <= MIN_RETRAIN_GAP_MINUTES;
};

const relTime = (iso) => {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
};

const fmtNext = (iso) => (iso ? new Date(iso).toLocaleString(undefined, {
  month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
}) : null);

// The warehouse_source table is "schema.table" (or "db.schema.table" for
// Snowflake) — drop the last segment to get a sensible default destination
// schema, so the field isn't left blank (BigQuery/Snowflake need it and
// crash/fail obscurely at run time without it).
const defaultSchemaFrom = (table) => {
  const parts = String(table || '').split('.');
  return parts.length > 1 ? parts.slice(0, -1).join('.') : '';
};

const anchorTextStyle = { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--fg)' };
const anchorInputStyle = { ...inputStyle, width: 'auto', padding: '5px 8px' };

const CadenceRow = ({ cadence, customDays, anchor, onCadence, onCustomDays, onAnchor, disabled }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <Segmented options={CADENCE_OPTIONS} value={cadence} onChange={onCadence} disabled={disabled} />
      {cadence === 'custom' && (
        <span style={anchorTextStyle}>
          every
          <input
            type="number"
            min={1}
            value={customDays ?? ''}
            onChange={(e) => onCustomDays(e.target.value ? Math.max(1, parseInt(e.target.value, 10) || 1) : '')}
            style={{ ...anchorInputStyle, width: 56, textAlign: 'center' }}
            disabled={disabled}
          />
          days
        </span>
      )}
    </div>
    {cadence !== 'off' && (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        {cadence === 'weekly' && (
          <span style={anchorTextStyle}>
            on
            <select
              value={anchor.weekday}
              onChange={(e) => onAnchor({ weekday: Number(e.target.value) })}
              style={anchorInputStyle}
              disabled={disabled}
            >
              {WEEKDAYS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
            </select>
          </span>
        )}
        {cadence === 'monthly' && (
          <span style={anchorTextStyle}>
            on day
            <select
              value={anchor.monthday}
              onChange={(e) => onAnchor({ monthday: Number(e.target.value) })}
              style={anchorInputStyle}
              disabled={disabled}
            >
              {/* Capped at 28 so the chosen day exists in every month. */}
              {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </span>
        )}
        <span style={anchorTextStyle}>
          at
          <input
            type="time"
            value={anchor.time}
            onChange={(e) => onAnchor({ time: e.target.value })}
            style={anchorInputStyle}
            disabled={disabled}
          />
        </span>
      </div>
    )}
  </div>
);

const ToggleRow = ({ checked, onChange, label, disabled }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 10, fontSize: 13.5,
    color: 'var(--fg)', userSelect: 'none',
  }}>
    <Toggle checked={checked} onChange={onChange} disabled={disabled} label={label} />
    {/* The text toggles too, the way a <label> would. */}
    <span
      onClick={() => !disabled && onChange(!checked)}
      style={{ cursor: disabled ? 'default' : 'pointer' }}
    >
      {label}
    </span>
  </div>
);

/**
 * Expandable checkbox list of Chronos-2's quantile levels — which of them a
 * scheduled forecast writes to the warehouse. Collapsed it shows only a
 * "n of 21 selected" summary (Section pattern from ArtifactsRail).
 */
const QuantileChecklist = ({ grid, selected, onChange }) => {
  const [open, setOpen] = useState(false);
  const allChecked = selected.length === grid.length;
  const someChecked = selected.length > 0 && !allChecked;
  const toggle = (q) => {
    onChange(selected.includes(q)
      ? selected.filter((v) => v !== q)
      : [...selected, q].sort((a, b) => a - b));
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div
        onClick={() => setOpen((o) => !o)}
        style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', userSelect: 'none' }}
      >
        <Icon name={open ? 'chevron-down' : 'chevron-right'} size={14} color="var(--fg-subtle)" />
        <span style={{ fontSize: 13.5, color: 'var(--fg)' }}>Forecast quantiles</span>
        <span style={{ marginLeft: 'auto', fontSize: 12.5, color: 'var(--fg-muted)' }}>
          {selected.length} of {grid.length} selected
        </span>
      </div>
      {open && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingLeft: 20 }}>
          <Checkbox
            checked={allChecked}
            indeterminate={someChecked}
            onChange={() => onChange(allChecked ? [] : [...grid])}
            label="All quantiles"
            style={{ fontSize: 13 }}
          />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
            {grid.map((q) => (
              <Checkbox
                key={q}
                checked={selected.includes(q)}
                onChange={() => toggle(q)}
                label={q.toFixed(2)}
                style={{ fontSize: 13, fontFamily: 'var(--font-mono)' }}
              />
            ))}
          </div>
          {selected.length === 0 && (
            <div style={{ ...captionStyle, color: 'var(--danger)' }}>Select at least one quantile.</div>
          )}
        </div>
      )}
      <div style={captionStyle}>
        These quantiles are written to your warehouse. The forecast chart always shows the
        median with a 90% range.
      </div>
    </div>
  );
};

/**
 * "Manage Pipeline" panel: recurring prediction/re-training schedules, an
 * optional warehouse writeback destination, a manual "Run pipeline now"
 * trigger with live stage progress, and the recent run history.
 */
const ManagePipelinePanel = ({ analysisId, onClose }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState(null);           // GET payload (schedulable, targets, key_candidates, ...)
  const [connections, setConnections] = useState([]);
  const [form, setForm] = useState(null);
  // Snapshot of `form` as of the last load/save — Save is disabled until the
  // user actually changes something relative to this baseline.
  const [savedForm, setSavedForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [runs, setRuns] = useState([]);
  const [running, setRunning] = useState(false);
  const pollRef = useRef(null);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const loadRuns = useCallback(() => {
    if (!analysisId) return;
    apiService.getPipelineRuns(analysisId)
      .then((resp) => {
        setRuns(resp.data.runs || []);
        setRunning(Boolean(resp.data.running?.inference || resp.data.running?.retrain));
      })
      .catch(() => {});
  }, [analysisId]);

  useEffect(() => {
    if (!analysisId) return;
    Promise.all([apiService.getPipelineSchedule(analysisId), apiService.listConnections()])
      .then(([schedResp, connResp]) => {
        const payload = schedResp.data;
        setMeta(payload);
        setRunning(Boolean(payload.running?.inference || payload.running?.retrain));
        setConnections((connResp.data.connections || []).filter((c) => c.status === 'ok'));
        const s = payload.schedule || {};
        // Never configured before: default to writing predictions back into
        // the warehouse the data came from. Destination fields are always
        // defaulted (connection/table/key) so turning the checkbox on later —
        // even on a schedule row that already exists from a previous save —
        // starts from a sensible, ready-to-save configuration rather than
        // blank required fields.
        const fresh = !payload.schedule;
        const initialForm = {
          inference_cadence: s.inference_cadence || 'off',
          inference_custom_days: s.inference_custom_days || '',
          inference_anchor: anchorFrom(s.next_inference_run_at),
          retrain_cadence: s.retrain_cadence || 'off',
          retrain_custom_days: s.retrain_custom_days || '',
          retrain_anchor: anchorFrom(s.next_retrain_run_at),
          predict_all_unlabeled: Boolean(s.predict_all_unlabeled),
          retrain_same_as_inference: Boolean(s.retrain_same_as_inference),
          write_predictions_enabled: fresh ? true : Boolean(s.write_predictions_enabled),
          destination_connection_id: s.destination_connection_id || payload.warehouse_source?.connection_id || '',
          destination_schema: s.destination_schema || defaultSchemaFrom(payload.warehouse_source?.table),
          destination_table: s.destination_table
            || (payload.is_timeseries ? `${TABLE_PREFIX}forecasts` : `${TABLE_PREFIX}predictions`),
          instance_key_column: s.instance_key_column || (payload.key_candidates || [])[0] || '',
          forecast_quantiles: s.forecast_quantiles || DEFAULT_QUANTILES,
        };
        setForm(initialForm);
        // A fresh, never-configured schedule already differs from "off/blank"
        // in ways the user hasn't chosen yet (defaulted destination fields) —
        // treat it as dirty so Save is available to persist those defaults.
        setSavedForm(fresh ? null : initialForm);
      })
      .catch(() => toast('Failed to load the pipeline settings.', 'error'))
      .finally(() => setLoading(false));
    loadRuns();
  }, [analysisId, toast, loadRuns]);

  // Poll run history while a run is in progress (live stage indicator).
  useEffect(() => {
    if (!running) return undefined;
    pollRef.current = setInterval(loadRuns, 2000);
    return () => clearInterval(pollRef.current);
  }, [running, loadRuns]);

  const isDirty = !savedForm || JSON.stringify(form) !== JSON.stringify(savedForm);

  const handleSave = () => {
    setSaving(true);
    const { inference_anchor, retrain_anchor, ...payload } = form;
    const ts = Boolean(meta?.is_timeseries);
    apiService.savePipelineSchedule(analysisId, {
      ...payload,
      inference_custom_days: form.inference_custom_days || null,
      retrain_custom_days: form.retrain_custom_days || null,
      inference_first_run_at: firstRunISO(form.inference_cadence, inference_anchor, form.inference_custom_days),
      retrain_first_run_at: (ts || form.retrain_same_as_inference)
        ? null
        : firstRunISO(form.retrain_cadence, retrain_anchor, form.retrain_custom_days),
      // Zero-shot forecasts have no retrain and no per-instance key — the
      // backend pins these off for TS too; sending them keeps the form state
      // honest after a save round-trip.
      ...(ts ? {
        retrain_cadence: 'off',
        retrain_same_as_inference: false,
        predict_all_unlabeled: false,
        instance_key_column: '',
      } : {}),
    })
      .then((resp) => {
        setMeta(resp.data);
        setSavedForm(form);
        toast('Pipeline schedule saved.', 'success');
        onClose && onClose();
      })
      .catch((e) => toast(e.response?.data?.detail || 'Failed to save the schedule.', 'error'))
      .finally(() => setSaving(false));
  };

  const handleRunNow = () => {
    apiService.runPipelineNow(analysisId)
      .then(() => {
        setRunning(true);
        toast('Pipeline run started.', 'success');
        setTimeout(loadRuns, 800);
      })
      .catch((e) => {
        if (e.response?.status === 409) toast('A pipeline run is already in progress.', 'error');
        else toast(e.response?.data?.detail || 'Could not start the pipeline run.', 'error');
      });
  };

  if (loading || !form) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--fg-muted)', fontSize: 13, padding: 8 }}>
        <Icon name="loader" size={15} /> Loading pipeline settings…
      </div>
    );
  }

  if (meta && !meta.schedulable) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--fg-strong)', fontSize: 14, fontWeight: 600 }}>
          <Icon name="workflow" size={15} /> Manage pipeline
        </div>
        <div style={{ fontSize: 13, color: 'var(--fg-muted)', lineHeight: 1.5 }}>
          {meta.not_schedulable_reason === 'no_timeseries_spec'
            ? 'Recurring forecasts become available once this analysis has completed a forecast on data imported from a connected data warehouse.'
            : 'Recurring runs become available once this analysis has a trained model on data imported from a connected data warehouse.'}
        </div>
      </div>
    );
  }

  const isTS = Boolean(meta?.is_timeseries);
  const scheduleConflict = !isTS && retrainTooCloseToInference(form);
  const quantileGrid = meta?.quantile_grid?.length ? meta.quantile_grid : QUANTILE_GRID;
  const noQuantiles = isTS && (form.forecast_quantiles || []).length === 0;
  const stageSteps = isTS ? TS_STAGE_STEPS : STAGE_STEPS;
  const activeRun = runs.find((r) => r.status === 'running');
  const sourceLabel = meta?.warehouse_source
    ? `${meta.warehouse_source.connection_name}${meta.warehouse_source.table ? ` · ${meta.warehouse_source.table}` : ''}`
    : '';
  const keyOptions = Array.from(new Set([
    ...(meta?.key_candidates || []),
    ...(form.instance_key_column ? [form.instance_key_column] : []),
  ]));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Icon name="workflow" size={15} color="var(--accent)" />
        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--fg-strong)' }}>Manage pipeline</span>
      </div>
      {sourceLabel && (
        <div style={{ ...captionStyle, marginTop: -8 }}>
          {isTS ? 'Fresh data is pulled from' : 'New instances are pulled from'}{' '}
          <span style={{ color: 'var(--fg)' }}>{sourceLabel}</span>.
        </div>
      )}

      {/* Scheduled predictions / forecasts */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Eyebrow>{isTS ? 'Scheduled forecasts' : 'Scheduled predictions'}</Eyebrow>
        <CadenceRow
          cadence={form.inference_cadence}
          customDays={form.inference_custom_days}
          anchor={form.inference_anchor}
          onCadence={(v) => set({ inference_cadence: v })}
          onCustomDays={(v) => set({ inference_custom_days: v })}
          onAnchor={(patch) => set({ inference_anchor: { ...form.inference_anchor, ...patch } })}
        />
        <div style={captionStyle}>
          {isTS
            ? 'The forecast re-runs on fresh warehouse data each time, covering the full horizon from that day.'
            : 'After a re-training, all unlabeled instances are re-scored once with the new model.'}
          {meta?.schedule?.next_inference_run_at && form.inference_cadence !== 'off'
            ? ` Next run: ${fmtNext(meta.schedule.next_inference_run_at)}.` : ''}
        </div>
        {!isTS && (
          <>
            <ToggleRow
              checked={form.predict_all_unlabeled}
              onChange={(v) => set({ predict_all_unlabeled: v })}
              label="Re-score every unlabeled instance every run (not just new ones)"
            />
            <div style={captionStyle}>
              {form.predict_all_unlabeled
                ? 'Every still-unlabeled instance is re-predicted on every run, even ones already '
                  + 'scored before — use this if an instance’s data can change while it waits on a '
                  + 'label, so its prediction should reflect the latest values. Costs more per run.'
                : 'Only instances that have never been predicted are scored — cheaper, but an instance '
                  + 'won’t be re-predicted if its data changes after its first prediction.'}
            </div>
          </>
        )}
      </div>

      {isTS && (
        <>
          <Divider />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Eyebrow>Quantiles</Eyebrow>
            <QuantileChecklist
              grid={quantileGrid}
              selected={form.forecast_quantiles || []}
              onChange={(qs) => set({ forecast_quantiles: qs })}
            />
          </div>
        </>
      )}

      {!isTS && (
        <>
          <Divider />

          {/* Re-training */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Eyebrow>Re-training</Eyebrow>
            <ToggleRow
              checked={form.retrain_same_as_inference}
              onChange={(v) => set({ retrain_same_as_inference: v })}
              label="Same schedule as predictions"
            />
            {!form.retrain_same_as_inference && (
              <CadenceRow
                cadence={form.retrain_cadence}
                customDays={form.retrain_custom_days}
                anchor={form.retrain_anchor}
                onCadence={(v) => set({ retrain_cadence: v })}
                onCustomDays={(v) => set({ retrain_custom_days: v })}
                onAnchor={(patch) => set({ retrain_anchor: { ...form.retrain_anchor, ...patch } })}
              />
            )}
            <div style={captionStyle}>
              {form.retrain_same_as_inference
                ? 'Re-training runs 30 minutes before each prediction run, on the same cadence — so the model is ready in time.'
                : 'You get an email when re-training moves the model metric by more than 5 percent points.'}
              {meta?.schedule?.next_retrain_run_at ? ` Next re-training: ${fmtNext(meta.schedule.next_retrain_run_at)}.` : ''}
            </div>
            {scheduleConflict && (
              <div style={{ ...captionStyle, color: 'var(--danger)' }}>
                Predictions and re-training are scheduled within {MIN_RETRAIN_GAP_MINUTES} minutes of each
                other — pick times at least {MIN_RETRAIN_GAP_MINUTES} minutes apart so a re-training run
                can't still be in progress when predictions are due.
              </div>
            )}
          </div>
        </>
      )}

      <Divider />

      {/* Warehouse writeback */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Eyebrow>{isTS ? 'Write forecasts to your warehouse' : 'Write predictions to your warehouse'}</Eyebrow>
        <ToggleRow
          checked={form.write_predictions_enabled}
          onChange={(v) => set({ write_predictions_enabled: v })}
          label={isTS ? 'Write forecasts to a table' : 'Write predictions to a table'}
        />
        {form.write_predictions_enabled && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <select
              value={form.destination_connection_id}
              onChange={(e) => set({ destination_connection_id: e.target.value })}
              style={inputStyle}
            >
              <option value="">Select a connection…</option>
              {connections.map((c) => (
                <option key={c.id} value={c.id}>{c.name} ({c.connection_type})</option>
              ))}
            </select>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="text"
                placeholder="DB.SCHEMA"
                value={form.destination_schema}
                onChange={(e) => set({ destination_schema: e.target.value })}
                style={{ ...inputStyle, flex: 1 }}
              />
              <div style={{ display: 'flex', flex: 1, minWidth: 0 }}>
                <span style={{
                  display: 'flex', alignItems: 'center', padding: '0 8px',
                  background: 'var(--bg-elev-2)', border: '1px solid var(--line-soft)', borderRight: 'none',
                  borderRadius: '8px 0 0 8px', color: 'var(--fg-muted)', fontSize: 13,
                  fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap',
                }}>
                  {TABLE_PREFIX}
                </span>
                <input
                  type="text"
                  placeholder="predictions"
                  value={form.destination_table.startsWith(TABLE_PREFIX)
                    ? form.destination_table.slice(TABLE_PREFIX.length)
                    : form.destination_table}
                  onChange={(e) => set({ destination_table: `${TABLE_PREFIX}${e.target.value}` })}
                  style={{ ...inputStyle, flex: 1, minWidth: 0, borderRadius: '0 8px 8px 0' }}
                />
              </div>
            </div>
            {!isTS && (
              <select
                value={form.instance_key_column}
                onChange={(e) => set({ instance_key_column: e.target.value })}
                style={inputStyle}
              >
                <option value="">Instance ID column…</option>
                {keyOptions.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            )}
            <div style={captionStyle}>
              {isTS
                ? 'Each run appends its forecast keyed by series, target, run date and period date — '
                  + 'overlapping horizons from successive runs are kept as separate vintages you can compare. '
                : 'Re-predicted instances overwrite their previous row. '}
              Table names are always prefixed with{' '}
              <code>{TABLE_PREFIX}</code>. The table is created if missing.
            </div>
          </div>
        )}
      </div>

      <Divider />

      {/* Run now + history */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Eyebrow>Runs</Eyebrow>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Button variant="outline" size="sm" onClick={handleRunNow} disabled={running}>
            <Icon name={running ? 'loader' : 'play'} size={14} />
            {running ? 'Running…' : 'Run pipeline now'}
          </Button>
        </div>
        {running && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              {stageSteps.map((step, i) => {
                const stageIdx = stageSteps.findIndex((st) => st.key === activeRun?.stage);
                const state = stageIdx < 0 ? (i === 0 ? 'active' : 'todo') : (i < stageIdx ? 'done' : i === stageIdx ? 'active' : 'todo');
                return (
                  <React.Fragment key={step.key}>
                    {i > 0 && <span style={{ color: 'var(--line-bright)', fontSize: 12 }}>→</span>}
                    <span style={{
                      fontSize: 12, fontWeight: state === 'active' ? 600 : 500,
                      color: state === 'done' ? 'var(--green)' : state === 'active' ? 'var(--accent)' : 'var(--fg-muted)',
                    }}>
                      {step.label}
                    </span>
                  </React.Fragment>
                );
              })}
            </div>
            {activeRun?.stage_detail && (
              <div style={{ fontSize: 11.5, color: 'var(--fg-subtle)', fontFamily: 'var(--font-mono)' }}>
                {activeRun.stage_detail}
              </div>
            )}
          </div>
        )}
        {runs.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {runs.slice(0, 5).map((r) => <RunRow key={r.run_id} run={r} />)}
          </div>
        )}
      </div>

      {isDirty && (
        <Button
          onClick={handleSave}
          disabled={saving || scheduleConflict || noQuantiles}
          title={scheduleConflict
            ? 'Predictions and re-training are scheduled too close together'
            : noQuantiles ? 'Select at least one forecast quantile' : undefined}
          style={{
            position: 'sticky', bottom: -18, zIndex: 5,
            width: 'calc(100% + 36px)', margin: '4px -18px -18px',
            borderRadius: '0 0 12px 12px',
          }}
        >
          {saving ? 'Saving…' : 'Save schedule'}
        </Button>
      )}
    </div>
  );
};

const RunRow = ({ run }) => {
  const [expanded, setExpanded] = useState(false);
  const color = STATUS_COLORS[run.status] || 'var(--fg-subtle)';
  const detail = run.run_type === 'retrain'
    ? (run.metric_old != null && run.metric_new != null
      ? `${run.metric_name}: ${Number(run.metric_old).toFixed(3)} → ${Number(run.metric_new).toFixed(3)}`
      : 'retrain')
    : run.run_type === 'forecast'
      ? [
        run.rows_predicted != null ? `${run.rows_predicted} forecast points` : '',
        run.warehouse_rows_written != null ? `${run.warehouse_rows_written} rows written` : '',
      ].filter(Boolean).join(' · ')
      : (run.rows_predicted != null ? `${run.rows_predicted} predicted` : '');
  const expandable = run.status === 'error' || (run.warnings || []).length > 0;
  return (
    <div
      onClick={expandable ? () => setExpanded((e) => !e) : undefined}
      style={{
        display: 'flex', flexDirection: 'column', gap: 3, padding: '6px 8px',
        borderRadius: 8, background: 'var(--bg-elev-2)', fontSize: 12.5,
        cursor: expandable ? 'pointer' : 'default',
      }}
      title={expandable ? 'Show details' : undefined}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: color, flexShrink: 0 }} />
        <span style={{ color: 'var(--fg)', fontWeight: 500 }}>
          {run.run_type === 'retrain' ? 'Re-training' : run.run_type === 'forecast' ? 'Forecast' : 'Predictions'}
        </span>
        <span style={{ color: 'var(--fg-muted)' }}>{run.trigger === 'manual' ? 'manual' : 'scheduled'}</span>
        <span style={{ color: 'var(--fg-muted)', marginLeft: 'auto' }}>{relTime(run.started_at)}</span>
      </div>
      <div style={{ display: 'flex', gap: 8, color: 'var(--fg-muted)', paddingLeft: 14 }}>
        {run.status === 'running' ? (run.stage || 'running') : detail}
        {run.status === 'error' && run.error_code && (
          <span style={{ color: 'var(--danger)' }}>{run.error_code}</span>
        )}
      </div>
      {expanded && (
        <div style={{ paddingLeft: 14, color: 'var(--fg-muted)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {run.status === 'error' && run.error ? run.error : null}
          {(run.warnings || []).map((w, i) => <div key={i}>• {w}</div>)}
        </div>
      )}
    </div>
  );
};

export default ManagePipelinePanel;
